'use client';

import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import {
  Infinity as InfinityIcon,
  Bookmark,
  ChevronsDown,
  Coins,
  Flag,
  Forward,
  Gem,
  Heart,
  Image as ImageIcon,
  List,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Mouse,
  PhoneCall,
  Send,
  Volume2,
  VolumeX,
  X,
  Zap,
} from 'lucide-react';

import { toast } from '@kit/ui/sonner';

import { transformedImageUrl } from '~/lib/image-cdn';

import { COMMENTER_HANDLES, COMMENT_TEXTS } from '../_lib/fake-comment-pool';
import type { FeedComment } from '../_lib/server/feed-social-actions';
import {
  addFeedComment,
  getFeedComments,
  submitFeedReport,
  toggleFeedLike,
} from '../_lib/server/feed-social-actions';
import type { FeedItem } from '../_lib/server/feed.loader';

/** Deterministic per-character seed so fake social numbers are stable. */
function seedHash(id: string) {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

const fakeLikesFor = (id: string) => 900 + (seedHash(id) % 8600);
const fakeSavesFor = (id: string) => 120 + (seedHash(id + 'save') % 1400);

/** 1234 -> "1.2K", 872 -> "872" — TikTok-style compact counts. */
function compact(n: number) {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k >= 10 ? Math.round(k) : k.toFixed(1)}K`;
}

/** FeedComment + presentation extras for the designed comment rows. */
type DisplayComment = FeedComment & {
  avatarUrl?: string;
  likes?: number;
  time?: string;
};

/** Total fake comments per character — ALSO the exact number of rows the
 * panel renders (list length matches the shown count 1:1). Capped at the
 * pool size so no text repeats within one character. */
const fakeCommentTotalFor = (id: string) =>
  60 + (seedHash(id + 'c') % (COMMENT_TEXTS.length - 60));

/** "2h" / "18h" / "3d" — older as you scroll, like a real comment feed. */
function fakeTimeFor(i: number, count: number) {
  const hours = 1 + Math.floor((i / Math.max(1, count - 1)) * 6 * 24);
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

function fakeCommentsFor(id: string): DisplayComment[] {
  const h = seedHash(id);
  const count = fakeCommentTotalFor(id);
  // Steps must be coprime to the pool sizes (180 texts, 80 handles) so the
  // walk visits every entry before cycling — no repeated text in one list.
  const tStep = [1, 7, 11, 13, 17, 19, 23, 29][h % 8]!;
  const nStep = [3, 7, 9, 11, 13, 17, 19, 21][(h >> 3) % 8]!;
  return Array.from({ length: count }, (_, i) => {
    const text = COMMENT_TEXTS[(h + i * tStep) % COMMENT_TEXTS.length]!;
    const who = COMMENTER_HANDLES[(h + i * nStep) % COMMENTER_HANDLES.length]!;
    // Top comments carry the likes; long tail fades toward zero.
    const likes = Math.max(
      0,
      Math.round((count * 1.6 + (h % 40)) * Math.pow(0.9, i) - (i % 3)),
    );
    return {
      id: `fake-${id}-${i}`,
      authorName: who.name,
      content: text,
      createdAt: '',
      avatarUrl: `https://randomuser.me/api/portraits/med/${who.av}.jpg`,
      likes,
      time: fakeTimeFor(i, count),
    };
  });
}

const REPORT_REASONS = [
  'Potential Underage Content',
  'Potential Deepfake Content',
  'Potential Violence or Gore',
  'Other Prohibited Content',
  'Incorrect Prohibited Content Flag',
  'Inaccurate Generation',
  'Other',
];

/** Free items a non-premium user can scroll before the premium gate. */
const FREE_SCROLLS = 5;

/** Drag distance (px) required to commit to the next/previous item. */
const DRAG_THRESHOLD = 90;
/** Wheel delta required to flip one item. */
const WHEEL_THRESHOLD = 120;
/** Transition duration for snapping (ms). */
const SNAP_MS = 450;
/** Comments sheet slide/backdrop animation duration (ms). */
const SHEET_MS = 320;

/**
 * TikTok-style vertical feed with rubber-band paging: the page follows the
 * finger/wheel, and only commits to the next item past a threshold —
 * otherwise it springs back to the item being watched. One item per gesture,
 * no free scrolling. Non-premium users hit a paywall card after FREE_SCROLLS
 * items. Action buttons are visual for now; Chat Now / comment deep-link to
 * the chat.
 */
export function FeedClient({
  items,
  isPremium,
  signedIn,
}: {
  items: FeedItem[];
  isPremium: boolean;
  signedIn: boolean;
}) {
  const router = useRouter();
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [menuFor, setMenuFor] = useState<{
    item: FeedItem;
    top: number;
    right: number;
  } | null>(null);

  const openMenuAt = (item: FeedItem, e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuFor({
      item,
      // Clamp so the popup never leaves the viewport.
      top: Math.min(rect.top, window.innerHeight - 140),
      right: window.innerWidth - rect.left + 10,
    });
  };
  const [reportFor, setReportFor] = useState<FeedItem | null>(null);
  const [commentsFor, setCommentsFor] = useState<FeedItem | null>(null);
  // Instagram-style: while the mobile sheet is up, the active media shrinks
  // into the space above it; sheetClosing lets the grow-back run in sync
  // with the sheet's slide-down instead of after it.
  const [sheetClosing, setSheetClosing] = useState(false);
  const [isPhone] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 639px)').matches,
  );
  // Locally-added comments per character (on top of fake + fetched ones).
  const [addedComments, setAddedComments] = useState<
    Record<string, FeedComment[]>
  >({});

  const isLiked = (item: FeedItem) => liked[item.id] ?? item.viewerLiked;

  const likeCount = (item: FeedItem) => {
    const base = fakeLikesFor(item.id) + item.realLikes;
    const now = isLiked(item);
    if (now && !item.viewerLiked) return base + 1;
    if (!now && item.viewerLiked) return base - 1;
    return base;
  };

  // Comments open/close shared by both layouts. Phone: portal sheet with its
  // own slide choreography. Desktop: inline panel in the item row. Closing
  // always animates (sheetClosing) before the state clears.
  const requestCloseComments = () => {
    setSheetClosing(true);
    setTimeout(() => {
      setCommentsFor(null);
      setSheetClosing(false);
    }, SHEET_MS);
  };
  const handleComments = (item: FeedItem) => {
    if (commentsFor?.id === item.id) {
      requestCloseComments();
    } else {
      setSheetClosing(false);
      setCommentsFor(item);
    }
  };

  const commentCount = (item: FeedItem) =>
    fakeCommentTotalFor(item.id) +
    item.realComments +
    (addedComments[item.id]?.length ?? 0);

  const handleLike = (item: FeedItem) => {
    if (!signedIn) {
      toast.error('Sign in to like');
      return;
    }
    const next = !isLiked(item);
    setLiked((l) => ({ ...l, [item.id]: next }));
    void toggleFeedLike(item.id).then((res) => {
      if ('error' in res) {
        setLiked((l) => ({ ...l, [item.id]: !next }));
        if (res.error === 'signin') toast.error('Sign in to like');
      }
    });
  };

  const handleShare = async (item: FeedItem) => {
    const url = `${window.location.origin}/catalog/${item.slug}`;
    setMenuFor(null);
    try {
      if (navigator.share) {
        await navigator.share({ title: `${item.name} — Edem AI`, url });
        return;
      }
      throw new Error('no-share');
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      } catch {
        toast.error('Could not copy the link');
      }
    }
  };
  // Videos start muted (autoplay policy); one toggle applies to the feed.
  const [muted, setMuted] = useState(true);

  const visible = isPremium ? items : items.slice(0, FREE_SCROLLS);
  const gated = !isPremium && items.length > FREE_SCROLLS;
  const total = visible.length + (gated ? 1 : 0);

  const [index, setIndex] = useState(0);
  // Live drag offset in px (0 when settled). Applied on top of the index
  // translate so the page visibly follows the finger, then springs back.
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef(new Map<number, HTMLVideoElement>());
  const indexRef = useRef(0);
  const animatingRef = useRef(false);
  const wheelAccumRef = useRef(0);
  const wheelIdleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartY = useRef(0);
  const touchDragRef = useRef(0);

  const goTo = (next: number) => {
    const clamped = Math.max(0, Math.min(total - 1, next));
    setDrag(0);
    setDragging(false);
    if (clamped === indexRef.current) return; // spring back
    indexRef.current = clamped;
    setIndex(clamped);
    animatingRef.current = true;
    setTimeout(() => {
      animatingRef.current = false;
    }, SNAP_MS + 60);
  };

  // Justified useEffect: the feed is a single full-screen pager. Lock the
  // document scroll while it's mounted so the page chrome (which is taller
  // than the viewport once the promo banner shows) can't produce a second
  // outer scrollbar next to the pager. Restored on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  // Justified useEffect: wheel must be a non-passive listener to preventDefault
  // (React's onWheel is passive), otherwise the page behind would scroll.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      // Comments open (desktop inline panel): let the wheel scroll the panel
      // natively — don't preventDefault or page the feed.
      if (commentsOpenRef.current) return;
      e.preventDefault();
      if (animatingRef.current) return;
      wheelAccumRef.current += e.deltaY;

      if (wheelIdleTimer.current) clearTimeout(wheelIdleTimer.current);
      wheelIdleTimer.current = setTimeout(() => {
        wheelAccumRef.current = 0;
      }, 180);

      if (wheelAccumRef.current > WHEEL_THRESHOLD) {
        wheelAccumRef.current = 0;
        goTo(indexRef.current + 1);
      } else if (wheelAccumRef.current < -WHEEL_THRESHOLD) {
        wheelAccumRef.current = 0;
        goTo(indexRef.current - 1);
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- total via refs
  }, [total]);

  // Justified useEffect: the autoplay attribute only acts at load time, so
  // videos scrolled into view later never start on their own — playback must
  // be driven imperatively as the active index changes.
  useEffect(() => {
    for (const [i, video] of videoRefs.current) {
      if (Math.abs(i - index) <= 1) {
        video.play().catch(() => {
          // Autoplay rejection (browser policy) — poster stays visible.
        });
        if (i !== index) video.currentTime = 0;
      } else {
        video.pause();
      }
    }
  }, [index]);

  // Ref mirror so the wheel listener (registered once) sees the live value.
  const commentsOpenRef = useRef(false);
  commentsOpenRef.current = commentsFor !== null;

  const onTouchStart = (e: React.TouchEvent) => {
    if (commentsFor) return;
    if (animatingRef.current) return;
    touchStartY.current = e.touches[0]?.clientY ?? 0;
    touchDragRef.current = 0;
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (commentsFor) return;
    if (!dragging) return;
    const y = e.touches[0]?.clientY ?? 0;
    let delta = y - touchStartY.current;
    // Resistance at the ends — the first/last item stretches, not moves.
    const atStart = indexRef.current === 0 && delta > 0;
    const atEnd = indexRef.current === total - 1 && delta < 0;
    if (atStart || atEnd) delta = delta * 0.3;
    touchDragRef.current = delta;
    setDrag(delta);
  };

  const onTouchEnd = () => {
    if (commentsFor) return;
    if (!dragging) return;
    const delta = touchDragRef.current;
    if (delta < -DRAG_THRESHOLD) goTo(indexRef.current + 1);
    else if (delta > DRAG_THRESHOLD) goTo(indexRef.current - 1);
    else {
      // Not far enough — spring back to the item being watched.
      setDrag(0);
      setDragging(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative flex-1 overflow-hidden overscroll-none"
      style={{ minHeight: 0, background: '#0b0712' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="absolute inset-0"
        style={{
          transform: `translateY(calc(${-index * 100}% + ${drag}px))`,
          transition: dragging
            ? 'none'
            : `transform ${SNAP_MS}ms cubic-bezier(0.22, 0.9, 0.3, 1)`,
        }}
      >
        {visible.map((item, i) => (
          <section
            key={item.id}
            className="relative flex items-center justify-center"
            style={{ height: '100%' }}
          >
            <div className="flex h-full w-full items-center justify-center gap-4 sm:py-6">
              {/* Card — full-bleed on mobile, contained portrait on desktop */}
              <div
                className="relative h-full w-full overflow-hidden sm:h-[92%] sm:w-auto sm:rounded-3xl"
                // Constant style keys on server AND client: gating the whole
                // object on isPhone caused a hydration mismatch, after which
                // React never wrote transformOrigin/transition to the real DOM
                // (only diffed keys land post-hydration) — the shrink anchored
                // to center. transform stays 'none' until the sheet opens, so
                // SSR and first client render agree byte-for-byte.
                style={{
                  transform:
                    isPhone && commentsFor?.id === item.id && !sheetClosing
                      ? 'translateY(1vh) scale(0.38)'
                      : 'none',
                  // x then y — anchors the shrunk media to the TOP of the page
                  transformOrigin: '50% 0%',
                  borderRadius:
                    isPhone && commentsFor?.id === item.id && !sheetClosing
                      ? 28
                      : undefined,
                  transition: `transform ${SHEET_MS}ms cubic-bezier(0.22, 0.9, 0.3, 1), border-radius ${SHEET_MS}ms ease`,
                }}
              >
                <div className="relative h-full w-full sm:aspect-[3/4] sm:w-auto">
                  {item.videoUrl ? (
                    <video
                      ref={(el) => {
                        if (el) videoRefs.current.set(i, el);
                        else videoRefs.current.delete(i);
                      }}
                      src={item.videoUrl}
                      // Same transform as the <img> branch and the page's
                      // <link rel="preload"> — full-res posters were multi-MB
                      // and the first poster is the LCP element.
                      poster={
                        transformedImageUrl(item.imageUrl, 1600) ??
                        item.imageUrl
                      }
                      autoPlay={i === 0}
                      preload={Math.abs(i - index) <= 2 ? 'auto' : 'metadata'}
                      muted={muted || i !== index}
                      loop
                      playsInline
                      className="h-full w-full object-cover object-top"
                    />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={
                        transformedImageUrl(item.imageUrl, 1600) ??
                        item.imageUrl
                      }
                      alt={item.name}
                      loading={i > 1 ? 'lazy' : undefined}
                      fetchPriority={i === 0 ? 'high' : undefined}
                      className="h-full w-full object-cover object-top"
                    />
                  )}

                  {/* Bottom scrim */}
                  <div
                    className="pointer-events-none absolute inset-x-0 bottom-0"
                    style={{
                      height: '45%',
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 55%, transparent 100%)',
                    }}
                  />

                  {/* Sound toggle for videos */}
                  {item.videoUrl && (
                    <button
                      type="button"
                      onClick={() => setMuted((m) => !m)}
                      aria-label={muted ? 'Unmute' : 'Mute'}
                      className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
                    >
                      {muted ? (
                        <VolumeX className="h-5 w-5" />
                      ) : (
                        <Volume2 className="h-5 w-5" />
                      )}
                    </button>
                  )}

                  {/* Info + CTA */}
                  <div
                    className="absolute right-16 bottom-5 left-5 transition-opacity duration-300 sm:right-5"
                    style={
                      isPhone && commentsFor?.id === item.id && !sheetClosing
                        ? { opacity: 0, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    <h2 className="text-2xl font-extrabold text-white drop-shadow sm:text-3xl">
                      {item.name.split(' ')[0]}
                      {item.age ? (
                        <>
                          ,{' '}
                          <span className="text-eros-pink font-extrabold">
                            {item.age}
                          </span>
                        </>
                      ) : null}
                    </h2>
                    {item.description && (
                      <p className="mt-1.5 line-clamp-2 max-w-md text-sm text-gray-200 drop-shadow">
                        {item.description}
                      </p>
                    )}
                    <Link
                      href={`/chat/${item.slug}`}
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#ec4899] to-[#a855f7] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(236,72,153,0.45)] transition-opacity hover:opacity-90"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat Now
                    </Link>
                  </div>

                  {/* Mobile action rail — inside the card, bottom right */}
                  <div
                    className="absolute right-3 bottom-24 flex flex-col items-center gap-3 transition-opacity duration-300 sm:hidden"
                    style={
                      isPhone && commentsFor?.id === item.id && !sheetClosing
                        ? { opacity: 0, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    <FeedAction
                      active={isLiked(item)}
                      activeClass="bg-eros-pink text-white"
                      label={compact(likeCount(item))}
                      onClick={() => handleLike(item)}
                    >
                      <Heart
                        className="h-5 w-5"
                        fill={isLiked(item) ? 'currentColor' : 'none'}
                      />
                    </FeedAction>
                    <FeedAction
                      label={compact(commentCount(item))}
                      onClick={() => handleComments(item)}
                    >
                      <MessageCircle className="h-5 w-5" />
                    </FeedAction>
                    <FeedAction
                      active={!!saved[item.id]}
                      activeClass="bg-white/25 text-white"
                      label={compact(
                        fakeSavesFor(item.id) + (saved[item.id] ? 1 : 0),
                      )}
                      onClick={() =>
                        setSaved((s) => ({ ...s, [item.id]: !s[item.id] }))
                      }
                    >
                      <Bookmark
                        className="h-5 w-5"
                        fill={saved[item.id] ? 'currentColor' : 'none'}
                      />
                    </FeedAction>
                    <FeedAction onClick={(e) => openMenuAt(item, e)}>
                      <MoreHorizontal className="h-5 w-5" />
                    </FeedAction>
                  </div>
                </div>
              </div>

              {/* Desktop action rail — floating beside the card */}
              <div className="hidden flex-col items-center gap-3 self-center sm:flex">
                <FeedAction
                  large
                  active={isLiked(item)}
                  activeClass="bg-eros-pink text-white shadow-[0_0_18px_rgba(236,72,153,0.5)]"
                  label={compact(likeCount(item))}
                  onClick={() => handleLike(item)}
                >
                  <Heart
                    className="h-5 w-5"
                    fill={isLiked(item) ? 'currentColor' : 'none'}
                  />
                </FeedAction>
                <FeedAction
                  large
                  label={compact(commentCount(item))}
                  onClick={() => handleComments(item)}
                >
                  <MessageCircle className="h-5 w-5" />
                </FeedAction>
                <FeedAction
                  large
                  active={!!saved[item.id]}
                  activeClass="bg-white/25 text-white"
                  label={compact(
                    fakeSavesFor(item.id) + (saved[item.id] ? 1 : 0),
                  )}
                  onClick={() =>
                    setSaved((s) => ({ ...s, [item.id]: !s[item.id] }))
                  }
                >
                  <Bookmark
                    className="h-5 w-5"
                    fill={saved[item.id] ? 'currentColor' : 'none'}
                  />
                </FeedAction>
                <FeedAction large onClick={(e) => openMenuAt(item, e)}>
                  <MoreHorizontal className="h-5 w-5" />
                </FeedAction>
              </div>

              {/* Desktop comments — inline sibling, opens NEXT TO the rail
                  (never over it): the wrapper's width animates 0 -> 400 and
                  the row re-centers around card + rail + panel. */}
              {!isPhone && (
                <div
                  className="hidden self-center overflow-hidden sm:block"
                  style={{
                    height: '92%',
                    width:
                      commentsFor?.id === item.id && !sheetClosing ? 400 : 0,
                    transition: `width ${SHEET_MS}ms cubic-bezier(0.22, 0.9, 0.3, 1)`,
                  }}
                >
                  {commentsFor?.id === item.id && (
                    <div
                      className="flex h-full w-[400px] flex-col overflow-hidden rounded-2xl border border-white/10"
                      style={{ background: '#15111c' }}
                    >
                      <CommentsBody
                        item={item}
                        signedIn={signedIn}
                        extraComments={addedComments[item.id] ?? []}
                        onAdd={(comment) =>
                          setAddedComments((m) => ({
                            ...m,
                            [item.id]: [...(m[item.id] ?? []), comment],
                          }))
                        }
                        onRequestClose={requestCloseComments}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Scroll hint on the first item (desktop) */}
            {i === 0 && (
              <div className="pointer-events-none absolute bottom-2 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-xs text-gray-400 sm:flex">
                <Mouse className="h-4 w-4" />
                Scroll for next
                <ChevronsDown className="h-4 w-4 animate-bounce" />
              </div>
            )}
          </section>
        ))}

        {/* Premium gate after the free scrolls — "Don't Stop Scrolling" */}
        {gated && (
          <section
            className="relative flex items-center justify-center px-4 py-4"
            style={{ height: '100%' }}
          >
            <div
              className="flex w-full max-w-md flex-col overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #14101f 0%, #0d0916 100%)',
                border: '1px solid rgba(168,85,247,0.35)',
                borderRadius: 24,
                // The section is exactly the visible feed area (the pager
                // track is absolutely positioned, so heights are definite);
                // cap to it and let the middle list scroll internally.
                maxHeight: '100%',
              }}
            >
              {/* Header banner — always visible */}
              <div
                className="shrink-0 px-6 py-4 text-center"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(168,85,247,0.45) 0%, rgba(168,85,247,0.10) 100%)',
                }}
              >
                <h2
                  className="font-extrabold text-white italic"
                  style={{
                    fontSize: 'clamp(22px, 3.5vh, 32px)',
                    lineHeight: 1.1,
                    letterSpacing: 1,
                  }}
                >
                  DON&apos;T STOP
                  <br />
                  SCROLLING
                </h2>
              </div>

              {/* Middle — scrolls internally when the viewport is short */}
              <div
                className="min-h-0 flex-1 overflow-y-auto px-6 pt-3 pb-2"
                style={{ overscrollBehavior: 'contain' }}
              >
                <p className="text-center text-sm leading-relaxed text-gray-300 italic">
                  You&apos;ve reached your free Feed limit. Upgrade to Premium
                  to continue exploring without interruptions and unlock the
                  complete Edem AI experience.
                </p>

                {/* Divider */}
                <div className="mt-3 mb-2 flex items-center gap-3">
                  <span className="h-px flex-1 bg-purple-400/30" />
                  <span className="flex items-center gap-1.5 text-sm font-bold text-white">
                    <span className="text-purple-400">✦</span>
                    Premium Includes
                    <span className="text-purple-400">✦</span>
                  </span>
                  <span className="h-px flex-1 bg-purple-400/30" />
                </div>

                {/* Feature rows */}
                <div className="divide-y divide-white/5">
                  {[
                    { Icon: Coins, label: '1,500 coins every month' },
                    { Icon: List, label: 'Unlimited Feed access' },
                    { Icon: InfinityIcon, label: 'Unlimited messages' },
                    {
                      Icon: ImageIcon,
                      label: 'Custom image and video generation',
                    },
                    { Icon: PhoneCall, label: 'Voice and video calls' },
                    {
                      Icon: Zap,
                      label: 'Faster generation and exclusive features',
                    },
                  ].map(({ Icon, label }) => (
                    <div key={label} className="flex items-center gap-3 py-2">
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                        style={{
                          background: 'rgba(168,85,247,0.15)',
                          border: '1px solid rgba(168,85,247,0.25)',
                        }}
                      >
                        <Icon className="h-5 w-5 text-purple-300" />
                      </span>
                      <span className="text-[15px] text-white">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA — pinned to the card bottom, never scrolled away */}
              <div className="shrink-0 px-6 pt-2 pb-8">
                <Link
                  href="/pricing"
                  className="flex w-full items-center justify-center gap-2 py-3.5 text-lg font-bold text-white transition-opacity hover:opacity-90"
                  style={{
                    background: 'linear-gradient(180deg, #c084fc, #9333ea)',
                    borderRadius: 16,
                    boxShadow:
                      '0 0 30px rgba(168,85,247,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  <Gem className="h-5 w-5" />
                  Unlock Premium
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Share / Report popup — anchored beside the pressed ⋯ button */}
      {menuFor &&
        createPortal(
          <div
            className="fixed inset-0 z-[300]"
            onClick={() => setMenuFor(null)}
          >
            <div
              className="absolute w-56 overflow-hidden rounded-2xl shadow-2xl"
              style={{
                background: '#26262b',
                top: menuFor.top,
                right: menuFor.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => void handleShare(menuFor.item)}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left text-[15px] text-white transition-colors hover:bg-white/5"
              >
                <Forward className="h-5 w-5" />
                Share
              </button>
              <div className="h-px bg-white/10" />
              <button
                type="button"
                onClick={() => {
                  setReportFor(menuFor.item);
                  setMenuFor(null);
                }}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left text-[15px] text-red-400 transition-colors hover:bg-white/5"
              >
                <Flag className="h-5 w-5" />
                Report
              </button>
            </div>
          </div>,
          document.body,
        )}

      {reportFor && (
        <ReportModal
          item={reportFor}
          signedIn={signedIn}
          onClose={() => setReportFor(null)}
        />
      )}

      {commentsFor && isPhone && (
        <CommentsSheet
          item={commentsFor}
          signedIn={signedIn}
          extraComments={addedComments[commentsFor.id] ?? []}
          onAdd={(comment) =>
            setAddedComments((m) => ({
              ...m,
              [commentsFor.id]: [...(m[commentsFor.id] ?? []), comment],
            }))
          }
          onClosingChange={setSheetClosing}
          onClose={() => {
            setCommentsFor(null);
            setSheetClosing(false);
          }}
        />
      )}
    </div>
  );
}

/** Report modal — mirrors the platform's content-report design. */
function ReportModal({
  item,
  signedIn,
  onClose,
}: {
  item: FeedItem;
  signedIn: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) {
      toast.error('Select a reason');
      return;
    }
    if (!signedIn) {
      toast.error('Sign in to report content');
      return;
    }
    setSubmitting(true);
    const res = await submitFeedReport(item.id, reason, details);
    setSubmitting(false);
    if ('success' in res) {
      toast.success('Report submitted — our team will review it');
      onClose();
    } else {
      toast.error(
        res.error === 'signin'
          ? 'Sign in to report content'
          : 'Could not submit the report',
      );
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Same backdrop treatment as the chat generate modals */}
      <div className="absolute inset-0 bg-black/80" />
      <div
        className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-black p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="flex items-center gap-2 text-xl font-bold text-white">
          <Flag className="text-eros-pink h-5 w-5" />
          Report Content
        </h2>
        <p className="mt-2 text-sm text-gray-400">
          Help us keep our platform safe by reporting inappropriate content.
        </p>
        <p className="mt-3 text-sm text-gray-300">
          Please let us know why you&apos;re reporting this:
        </p>

        <div className="mt-4 flex flex-col gap-3">
          {REPORT_REASONS.map((r) => (
            <label
              key={r}
              className="flex cursor-pointer items-center gap-3 text-[15px] text-white"
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2"
                style={{ borderColor: '#ec4899' }}
              >
                {reason === r && (
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: '#ec4899' }}
                  />
                )}
              </span>
              <input
                type="radio"
                name="report-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
                className="sr-only"
              />
              {r}
            </label>
          ))}
        </div>

        <p className="mt-5 text-sm font-semibold text-white">
          Any additional information that would help us:
        </p>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Tell us more about why you're reporting this…"
          rows={4}
          className="focus:border-eros-pink mt-2 w-full resize-y rounded-xl border border-white/15 bg-transparent p-3 text-sm text-white placeholder-gray-500 outline-none"
        />

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: '#ec4899' }}
          >
            {submitting ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Comments sheet — fake seed comments + real ones + the viewer's own. */
/** Phone-only sheet shell: swipes up from below in sync with the media
 * shrink; the panel content itself lives in CommentsBody (shared with the
 * desktop inline panel). */
function CommentsSheet({
  item,
  signedIn,
  extraComments,
  onAdd,
  onClose,
  onClosingChange,
}: {
  item: FeedItem;
  signedIn: boolean;
  extraComments: FeedComment[];
  onAdd: (comment: FeedComment) => void;
  onClose: () => void;
  onClosingChange?: (closing: boolean) => void;
}) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const requestClose = () => {
    setEntered(false);
    // Let the media grow back in sync with the sheet sliding away.
    onClosingChange?.(true);
    setTimeout(onClose, SHEET_MS);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-end justify-center"
      onClick={requestClose}
    >
      <div
        className="relative z-10 flex w-full flex-col overflow-hidden rounded-t-3xl border border-white/10"
        style={{
          background: '#15111c',
          maxHeight: '58vh',
          transform: entered ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${SHEET_MS}ms cubic-bezier(0.22, 0.9, 0.3, 1)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grab handle */}
        <div className="pt-2" aria-hidden="true">
          <div className="mx-auto h-1 w-12 rounded-full bg-white/25" />
        </div>
        <CommentsBody
          item={item}
          signedIn={signedIn}
          extraComments={extraComments}
          onAdd={onAdd}
          onRequestClose={requestClose}
        />
      </div>
    </div>,
    document.body,
  );
}

/** Comments panel content — header, list (fake seed + real + own), input.
 * Rendered inside the phone sheet AND the desktop inline panel. */
function CommentsBody({
  item,
  signedIn,
  extraComments,
  onAdd,
  onRequestClose,
}: {
  item: FeedItem;
  signedIn: boolean;
  extraComments: FeedComment[];
  onAdd: (comment: FeedComment) => void;
  onRequestClose: () => void;
}) {
  const [fetched, setFetched] = useState<FeedComment[] | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [likedComments, setLikedComments] = useState<Record<string, boolean>>(
    {},
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Justified useEffect: fetch the character's real comments when the panel
  // opens — a one-shot load tied to the panel's lifetime.
  useEffect(() => {
    let alive = true;
    getFeedComments(item.id).then((list) => {
      if (alive) setFetched(list);
    });
    return () => {
      alive = false;
    };
  }, [item.id]);

  const send = async () => {
    const content = draft.trim();
    if (!content) return;
    if (!signedIn) {
      toast.error('Sign in to comment');
      return;
    }
    setSending(true);
    const res = await addFeedComment(item.id, content);
    setSending(false);
    if ('comment' in res) {
      onAdd(res.comment);
      setDraft('');
    } else {
      toast.error(
        res.error === 'signin' ? 'Sign in to comment' : 'Could not comment',
      );
    }
  };

  const comments: DisplayComment[] = [
    ...fakeCommentsFor(item.id),
    ...(fetched ?? []),
    ...extraComments.filter((c) => !fetched?.some((f) => f.id === c.id)),
  ];
  const totalShown =
    fakeCommentTotalFor(item.id) +
    (fetched?.length ?? 0) +
    extraComments.length;

  return (
    <>
      <div className="flex items-center justify-between px-5 py-4">
        <h3 className="text-lg font-bold text-white">
          Comments{' '}
          <span className="font-semibold text-gray-400">({totalShown})</span>
        </h3>
        <button
          type="button"
          onClick={onRequestClose}
          aria-label="Close comments"
          className="text-gray-400 transition-colors hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-4">
        {fetched === null && (
          <div className="flex justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
          </div>
        )}
        <div className="flex flex-col gap-5">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-3">
              {c.avatarUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={c.avatarUrl}
                  alt=""
                  width={36}
                  height={36}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-9 w-9 shrink-0 rounded-full object-cover"
                  onError={(e) => {
                    // Stock portrait unreachable — swap to initials.
                    (e.currentTarget as HTMLImageElement).style.display =
                      'none';
                    e.currentTarget.nextElementSibling?.classList.remove(
                      'hidden',
                    );
                  }}
                />
              ) : null}
              <span
                className={`from-eros-pink to-eros-purple h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-bold text-white ${
                  c.avatarUrl ? 'hidden' : 'flex'
                }`}
              >
                {c.authorName.charAt(0).toUpperCase()}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-white">
                  {c.authorName}
                  {c.time ? (
                    <span className="ml-2 font-normal text-gray-500">
                      {c.time}
                    </span>
                  ) : null}
                </p>
                <p className="mt-0.5 text-sm break-words text-gray-200">
                  {c.content}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDraft((d) =>
                      d.startsWith('@') ? d : `@${c.authorName} ${d}`,
                    );
                    inputRef.current?.focus();
                  }}
                  className="mt-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-300"
                >
                  Reply
                </button>
              </div>

              {c.likes !== undefined && (
                <button
                  type="button"
                  onClick={() =>
                    setLikedComments((s) => ({ ...s, [c.id]: !s[c.id] }))
                  }
                  aria-label="Like comment"
                  className="flex shrink-0 flex-col items-center gap-0.5 pt-1"
                >
                  <Heart
                    className={`h-4 w-4 transition-colors ${
                      likedComments[c.id]
                        ? 'text-eros-pink'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                    fill={likedComments[c.id] ? 'currentColor' : 'none'}
                  />
                  <span className="text-[11px] text-gray-500">
                    {c.likes + (likedComments[c.id] ? 1 : 0)}
                  </span>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-white/10 px-4 py-3">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
          placeholder={
            signedIn ? 'Write a comment…' : 'Sign in to leave a comment'
          }
          className="focus:border-eros-pink flex-1 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-gray-500 outline-none"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={sending || !draft.trim()}
          aria-label="Send comment"
          className="from-eros-pink to-eros-purple flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </>
  );
}

function FeedAction({
  children,
  onClick,
  active = false,
  activeClass = '',
  large = false,
  label,
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  active?: boolean;
  activeClass?: string;
  large?: boolean;
  label?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={onClick}
        className={`flex items-center justify-center rounded-full transition-colors ${
          active ? activeClass : 'bg-black/45 text-white hover:bg-black/65'
        }`}
        style={{
          width: large ? 48 : 42,
          height: large ? 48 : 42,
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        {children}
      </button>
      {label !== undefined && (
        <span className="text-xs font-semibold text-white drop-shadow">
          {label}
        </span>
      )}
    </div>
  );
}
